import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import Modal from '../../components/Modal';
import PlateformService from '../../components/PlateformService';
import StoreService from '../../components/StoreService';
import { useNavigate } from 'react-router-dom';

const emptyPlateform = {
  name: '',
  login: '',
  password: '',
  additionnal_data: '',
  type: 'none',
  store_ids: [],
};

function PlateformsView() {
  const [plateforms, setPlateforms] = useState([]);
  const [formData, setFormData] = useState(emptyPlateform);
  const [editingId, setEditingId] = useState(null);
  const [jsonError, setJsonError] = useState('');
  const [testStatus, setTestStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [aimerLoginStatus, setAimerLoginStatus] = useState(null);
  const [aimerLoggingIn, setAimerLoggingIn] = useState(false);

  const navigate = useNavigate();

  const fetchPlateforms = async () => {
    try {
      setLoading(true);
      const data = await PlateformService.fetchPlateforms();
      setPlateforms(data.response || data);
    } catch (error) {
      console.error('fetchPlateforms error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStores = async () => {
    try {
      const data = await StoreService.fetchStores();
      setStores(data.response || data);
    } catch (error) {
      console.error('fetchStores error:', error);
    }
  };

  useEffect(() => {
    fetchPlateforms();
    fetchStores();
  }, []);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const extractStoreId = (store) => {
    if (!store) return '';
    if (typeof store === 'string') return store;
    return store._id || store.id || '';
  };

  const storeOptions = useMemo(
    () =>
      stores.map((store) => {
        const value = extractStoreId(store);
        return {
          value,
          label: store.name || store.store_name || store.code || value,
        };
      }),
    [stores]
  );

  const resolveStoreName = (storeId) => {
    const match = stores.find((store) => extractStoreId(store) === storeId);
    if (match) {
      return match.name || match.store_name || match.code || storeId;
    }
    return storeId;
  };

  const selectedStoreOptions = useMemo(
    () =>
      Array.isArray(formData.store_ids)
        ? formData.store_ids
            .map((id) =>
              storeOptions.find((option) => option.value === id) || {
                value: id,
                label: resolveStoreName(id),
              }
            )
        : [],
    [formData.store_ids, storeOptions]
  );

  const handleStoreSelection = (selected) => {
    const selectedValues = Array.isArray(selected)
      ? selected.map((option) => option.value)
      : [];
    setFormData((prev) => ({
      ...prev,
      store_ids: selectedValues,
    }));
  };

  const sanitizePayload = useMemo(
    () => (data) => {
      const payload = {
        name: data.name.trim(),
        login: data.login.trim(),
        additionnal_data: {},
        type: (data.type || 'none').trim() || 'none',
        store_ids: Array.isArray(data.store_ids)
          ? data.store_ids.filter(Boolean)
          : [],
      };

      if (data.password && data.password.trim()) {
        payload.password = data.password.trim();
      }

      if (data.additionnal_data && data.additionnal_data.trim()) {
        payload.additionnal_data = JSON.parse(data.additionnal_data);
      }

      if (!Object.keys(payload.additionnal_data).length) {
        delete payload.additionnal_data;
      }

      return payload;
    },
    []
  );

  const handleSubmit = async () => {
    try {
      setJsonError('');
      const payload = sanitizePayload(formData);
      if (editingId) await PlateformService.updatePlateform(editingId, payload);
      else await PlateformService.createPlateform(payload);
      setFormData({ ...emptyPlateform });
      setEditingId(null);
      await fetchPlateforms();
    } catch (error) {
      if (error instanceof SyntaxError) {
        setJsonError('Les données additionnelles doivent être un JSON valide.');
      } else {
        console.error(error);
      }
    }
  };

  const handleEdit = (plateform) => {
    setEditingId(plateform._id || plateform.id);
    const storeIds = Array.isArray(plateform.store_ids)
      ? plateform.store_ids.map((store) => extractStoreId(store)).filter(Boolean)
      : [];
    setFormData({
      name: plateform.name || '',
      login: plateform.login || '',
      password: '',
      additionnal_data: plateform.additionnal_data
        ? JSON.stringify(plateform.additionnal_data, null, 2)
        : '',
      type: plateform.type || 'none',
      store_ids: storeIds,
    });
    setJsonError('');
    setTestStatus(null);
    setAimerLoginStatus(null);
  };

  const handleDelete = async (id) => {
    await PlateformService.deletePlateform(id);
    fetchPlateforms();
  };

  const handleAimerLogin = async (plateform) => {
    const plateformId = plateform?._id || plateform?.id;
    const payload = {};

    if (plateformId) {
      payload.plateform_id = plateformId;
    }

    if (plateform?.name) {
      payload.plateform_name = plateform.name;
    }

    try {
      setAimerLoggingIn(true);
      setAimerLoginStatus(null);

      const response = await PlateformService.loginAimer(payload);

      const source = response?.source ? ` (${response.source})` : '';
      setAimerLoginStatus({
        type: 'success',
        message: `Connexion à aimer.ma réussie${source}.`,
        response,
      });
    } catch (error) {
      console.error(error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        'Échec de la connexion à aimer.ma.';
      setAimerLoginStatus({ type: 'danger', message });
    } finally {
      setAimerLoggingIn(false);
    }
  };

  const handleTestConnection = async (plateform) => {
    const normalizedName = (plateform?.name || '').toLowerCase();
    const normalizedType = (plateform?.type || '').toLowerCase();
    let errorMessage = 'Échec du test de connexion.';
    try {
      setTesting(true);
      setTestStatus(null);

      let response;
      let successMessage = 'Test de connexion réussi.';

      if (normalizedName === 'glovo') {
        response = await PlateformService.testGlovoConnection();
        successMessage =
          (response && typeof response === 'object' && response.message) ||
          (typeof response === 'string'
            ? response
            : 'Connexion Glovo réussie.');
        errorMessage = 'Échec du test de connexion Glovo.';
      } else if (normalizedType === 'kooul' || normalizedName.startsWith('kooul_')) {
        const storeId = plateform.name?.startsWith('kooul_')
          ? plateform.name.slice('kooul_'.length).trim()
          : plateform?.additionnal_data?.kooul?.store_id ||
            (Array.isArray(plateform?.store_ids) && plateform.store_ids[0]) ||
            '';
        const normalizedStoreId = String(storeId || '').trim();
        if (!normalizedStoreId) {
          throw new Error(
            'Impossible de déterminer le storeId pour ce test de connexion Kooul.'
          );
        }
        response = await PlateformService.testKooulConnection({ storeId: normalizedStoreId });
        successMessage =
          (response && typeof response === 'object' && response.message) ||
          'Connexion Kooul réussie.';
        errorMessage = 'Échec du test de connexion Kooul.';
      } else {
        throw new Error("Aucun test de connexion disponible pour cette plateforme.");
      }

      setTestStatus({ type: 'success', message: successMessage, response });
    } catch (error) {
      console.error(error);
      const message =
        error?.response?.data?.message || error.message || errorMessage;
      setTestStatus({ type: 'danger', message });
    } finally {
      setTesting(false);
    }
  };

  const isSubmitDisabled =
    !formData.name.trim() ||
    !formData.login.trim() ||
    (!editingId && !formData.password.trim());

  return (
    <div className="container mt-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3">
        <h2 className="mb-0">Plateformes</h2>
        <div className="text-md-end">
          <button
            className="btn btn-outline-secondary me-2"
            onClick={fetchPlateforms}
            disabled={loading}
          >
            {loading ? 'Actualisation…' : 'Rafraîchir'}
          </button>
          <button
            className="btn btn-success"
            data-bs-toggle="modal"
            data-bs-target="#plateformModal"
            onClick={() => {
              setFormData({ ...emptyPlateform });
              setEditingId(null);
              setJsonError('');
              setTestStatus(null);
              setAimerLoginStatus(null);
            }}
          >
            Ajouter une plateforme
          </button>
        </div>
      </div>
      {testStatus && (
        <div className={`alert alert-${testStatus.type}`} role="alert">
          <div>{testStatus.message}</div>
          {testStatus.response && typeof testStatus.response === 'object' && (
            <pre className="mb-0 mt-2">
              {JSON.stringify(testStatus.response, null, 2)}
            </pre>
          )}
        </div>
      )}
      {aimerLoginStatus && (
        <div className={`alert alert-${aimerLoginStatus.type}`} role="alert">
          <div>{aimerLoginStatus.message}</div>
          {aimerLoginStatus.response &&
            typeof aimerLoginStatus.response === 'object' && (
              <pre className="mb-0 mt-2">
                {JSON.stringify(aimerLoginStatus.response, null, 2)}
              </pre>
            )}
        </div>
      )}
      {aimerLoginStatus && (
        <div className={`alert alert-${aimerLoginStatus.type}`} role="alert">
          <div>{aimerLoginStatus.message}</div>
          {aimerLoginStatus.response &&
            typeof aimerLoginStatus.response === 'object' && (
              <pre className="mb-0 mt-2">
                {JSON.stringify(aimerLoginStatus.response, null, 2)}
              </pre>
            )}
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-bordered table-striped">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Type</th>
              <th>Identifiant</th>
              <th>Magasins liés</th>
              <th>Connecté</th>
              <th>Dernière connexion</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="7" className="text-center">
                  Chargement…
                </td>
              </tr>
            )}
            {!loading && plateforms.length === 0 && (
              <tr>
                <td colSpan="7" className="text-center">
                  Aucune plateforme disponible.
                </td>
              </tr>
            )}
            {!loading &&
              plateforms.map((p) => {
                const storeNames = Array.isArray(p.store_ids)
                  ? p.store_ids
                      .map((store) => resolveStoreName(extractStoreId(store)))
                      .filter(Boolean)
                  : [];
                return (
                  <tr key={p._id || p.id}>
                    <td>{p.name}</td>
                    <td>{p.type || 'none'}</td>
                    <td>{p.login}</td>
                    <td>{storeNames.length ? storeNames.join(', ') : '—'}</td>
                    <td>{p.is_connected ? 'Oui' : 'Non'}</td>
                    <td>
                      {p.last_connection
                        ? new Date(p.last_connection).toLocaleString()
                        : '—'}
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-warning me-1"
                        data-bs-toggle="modal"
                        data-bs-target="#plateformModal"
                        onClick={() => handleEdit(p)}
                      >
                        Modifier
                      </button>
                      {p.name &&
                        (p.name.toLowerCase() === 'glovo' ||
                          (p.type && p.type.toLowerCase() === 'kooul') ||
                          p.name.toLowerCase().startsWith('kooul_')) && (
                          <button
                            className="btn btn-sm btn-info me-1"
                            onClick={() => handleTestConnection(p)}
                            disabled={testing}
                          >
                            {testing ? 'Test en cours…' : 'Tester la connexion'}
                          </button>
                        )}
                      {p.name &&
                        ((p.type && p.type.toLowerCase() === 'kooul') ||
                          p.name.toLowerCase().startsWith('kooul_')) && (
                        <button
                          className="btn btn-sm btn-outline-secondary me-1"
                          onClick={() =>
                            navigate('/plateforms/kooul', {
                              state: {
                                plateformId: p._id || p.id,
                                plateformName: p.name,
                              },
                            })
                          }
                        >
                          Ouvrir la plateforme
                        </button>
                      )}
                      {p.name && p.name.toLowerCase() === 'aimer.ma' && (
                        <>
                          <button
                            className="btn btn-sm btn-primary me-1"
                            onClick={() => handleAimerLogin(p)}
                            disabled={aimerLoggingIn}
                          >
                            {aimerLoggingIn
                              ? 'Connexion en cours…'
                              : 'Connexion aimer.ma'}
                          </button>
                          <button
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() =>
                              navigate('/plateforms/aimer', {
                                state: {
                                  plateformId: p._id || p.id,
                                  plateformName: p.name,
                                  plateformData: p,
                                },
                              })
                            }
                          >
                            Ouvrir la plateforme
                          </button>
                        </>
                      )}
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(p._id || p.id)}
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <Modal
        modalId="plateformModal"
        onSubmit={handleSubmit}
        submitLabel={editingId ? 'Modifier' : 'Ajouter'}
        submitDisabled={isSubmitDisabled}
        title={editingId ? 'Modifier la plateforme' : 'Ajouter une plateforme'}
      >
        <form>
          <div className="mb-3">
            <label className="form-label">Nom</label>
            <input
              className="form-control"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Nom"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Identifiant</label>
            <input
              className="form-control"
              name="login"
              value={formData.login}
              onChange={handleChange}
              placeholder="Identifiant"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Type</label>
            <input
              className="form-control"
              name="type"
              value={formData.type}
              onChange={handleChange}
              placeholder="Type (glovo, kooul, …)"
            />
            <div className="form-text">
              Laissez vide pour utiliser "none" par défaut.
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label">Mot de passe</label>
            <input
              type="password"
              className="form-control"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Mot de passe"
            />
            {editingId && (
              <small className="text-muted">
                Laissez vide pour conserver le mot de passe actuel.
              </small>
            )}
          </div>
          <div className="mb-3">
            <label className="form-label">Données additionnelles (JSON)</label>
            <textarea
              className={`form-control ${jsonError ? 'is-invalid' : ''}`}
              rows={5}
              name="additionnal_data"
              value={formData.additionnal_data}
              onChange={handleChange}
              placeholder="{ }"
            />
            {jsonError && <div className="invalid-feedback">{jsonError}</div>}
          </div>
          <div className="mb-3">
            <label className="form-label">Magasins associés</label>
            <Select
              inputId="plateform-store-ids"
              isMulti
              options={storeOptions}
              value={selectedStoreOptions}
              onChange={handleStoreSelection}
              placeholder="Sélectionnez un ou plusieurs magasins"
              classNamePrefix="react-select"
              noOptionsMessage={() => 'Aucun magasin trouvé'}
            />
            <div className="form-text">
              Recherchez et sélectionnez un ou plusieurs magasins pour lier la plateforme.
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default PlateformsView;
